import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { RefreshCw } from "lucide-react";
import { driverIcon, fetchDrivers, fetchRides } from "./utils";

export function LiveMapPanel({ auth }) {
  const [drivers, setDrivers] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch held + in_progress rides — driver_ride_status can be 'in_progress'
      // on either (started_at is set but top-level status may still be 'held').
      const [dRes, heldRes, ipRes] = await Promise.all([
        fetchDrivers(auth),
        fetchRides(auth, "held").catch(() => ({ rides: [] })),
        fetchRides(auth, "in_progress").catch(() => ({ rides: [] })),
      ]);
      setDrivers(dRes.drivers || []);
      setActiveRides([...(heldRes.rides || []), ...(ipRes.rides || [])]);
      setLastRefresh(new Date());
    } catch (err) {
      toast.error(err.message || "Failed to load drivers");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Location sharing is gated to en_route/arrived/in_progress rides (privacy
  // rule). Show every driver whose CURRENT ride is one of those states.
  // Filter client-side because /api/nemt/rides?status=... queries the top-level
  // 'status' field (payment lifecycle), NOT driver_ride_status (driver state).
  const LIVE_STATES = ["en_route", "arrived", "in_progress"];
  const trackableDriverIds = new Set(
    activeRides
      .filter((r) => LIVE_STATES.includes(r.driver_ride_status))
      .map((r) => r.driver_id)
      .filter(Boolean)
  );
  const withLocation = drivers.filter(
    (d) => d.active
      && d.status && d.status !== "offline"
      && d.last_location?.lat && d.last_location?.lng
      && trackableDriverIds.has(d.id)
  );
  const defaultCenter = [33.749, -84.388];
  const center = withLocation.length ? [withLocation[0].last_location.lat, withLocation[0].last_location.lng] : defaultCenter;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-brand-navy/10 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-serif text-xl text-brand-deep">Live driver map</div>
          <div className="text-xs text-brand-muted mt-1">
            {withLocation.length} of {drivers.filter((d) => d.active).length} active driver{drivers.filter((d) => d.active).length === 1 ? "" : "s"} online now
            {lastRefresh && ` · updated ${lastRefresh.toLocaleTimeString()}`}
          </div>
        </div>
        <button onClick={load} disabled={loading} data-testid="map-refresh" className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium px-4 py-2 rounded-full transition disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-brand-navy/10 rounded-2xl overflow-hidden" style={{ height: "70vh" }} data-testid="live-map-container">
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {withLocation.map((d) => {
            // Prefer the most-advanced ride so the popup reflects the current
            // trip rather than a stale earlier ride.
            const ride =
              activeRides.find((r) => r.driver_id === d.id && r.driver_ride_status === "in_progress") ||
              activeRides.find((r) => r.driver_id === d.id && r.driver_ride_status === "arrived") ||
              activeRides.find((r) => r.driver_id === d.id && r.driver_ride_status === "en_route") ||
              activeRides.find((r) => r.driver_id === d.id);
            const passengerLabel =
              ride?.driver_ride_status === "in_progress" ? "On board"
              : ride?.driver_ride_status === "arrived" ? "At pickup"
              : ride?.driver_ride_status === "en_route" ? "Headed to"
              : "Ride";
            return (
              <Marker key={d.id} position={[d.last_location.lat, d.last_location.lng]} icon={driverIcon}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.phone}</div>
                    <div className="mt-1"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold ${d.status === "busy" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{d.status || "offline"}</span></div>
                    {ride && (
                      <div className="mt-2 pt-2 border-t border-slate-200 text-xs" data-testid={`map-passenger-${d.id}`}>
                        <div className="font-semibold text-emerald-800">
                          {passengerLabel}: {ride.member_name} · {ride.passengers || 1} pax
                        </div>
                        <div className="text-slate-600 mt-0.5">
                          {ride.driver_ride_status === "en_route"
                            ? <>→ Pickup: {ride.pickup}</>
                            : <>→ {ride.dropoff}</>}
                        </div>
                        {ride.driver_ride_status && (
                          <div className="text-[10px] uppercase text-indigo-700 mt-0.5">
                            {ride.driver_ride_status === "en_route" ? "on the way" : ride.driver_ride_status.replace(/_/g, " ")}
                          </div>
                        )}
                      </div>
                    )}
                    {d.car_make && <div className="mt-1 text-xs">{[d.car_year, d.car_color, d.car_make, d.car_model].filter(Boolean).join(" ")}</div>}
                    {d.car_tag && <div className="text-xs font-mono">Tag: {d.car_tag}</div>}
                    <div className="text-[11px] text-slate-500 mt-1">Updated {new Date(d.last_location.ts).toLocaleTimeString()}</div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {!withLocation.length && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900">
          No drivers are online right now. Drivers appear here only after signing in, allowing location, and going on-shift (status: available or busy).
        </div>
      )}
    </div>
  );
}
